import { sendLoggedInEvent, sendViewedPageEvent } from '@/utils/mixpanel'
import * as Sentry from '@sentry/vue'
import { isObject, useStorage } from '@vueuse/core'
import { setupLayouts } from 'virtual:generated-layouts'
import routes from 'virtual:generated-pages'
import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import { ENTRY_BY_TYPE } from './calendar-types'
import { useAppStore } from './store/app'
import { useAuthStore } from './store/auth'
import { useCalendarStore } from './store/calendar'
import { useFreemiumPostsDialogStore } from './store/freemium-posts-dialog'
import './style.css'
import { IOS_ERROR } from './types'
import { endpoints } from './utils/endpoints'
import { sendViewedCalendarPageEvent } from './utils/mixpanel-calendar'
import Clarity from '@microsoft/clarity'

import { createPinia } from 'pinia'

interface CustomWindow extends Window {
  webkit?: any
}
declare const window: CustomWindow

const deviceTokenStore = useStorage('device-token', '')
const deviceInfoStore = useStorage('device_info', '')
let pendingRedirect: string | null = null

document.addEventListener('gesturestart', (e) => e.preventDefault())

window.addEventListener('vite:preloadError', () => {
  window.location.reload()
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: setupLayouts(routes),
})

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)
app.use(router)

const authStore = useAuthStore()
const appStore = useAppStore()

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration({ router })],
  tracesSampleRate: import.meta.env.PROD ? 0.5 : 1.0,
  ignoreErrors: [
    'AbortError: The play() request was interrupted', // nothing we can do about it
    'ResizeObserver loop limit exceeded', // safe to ignore
  ],
})

window.addEventListener(
  'message',
  (
    event: MessageEvent<{
      processingEntryIds: string[]
      showFreemiumPopUpFacebook: boolean
      showFreemiumPopUpInstagram: boolean
      spotHopper?: any
      inquiryFilter?: any
    }>,
  ) => {
    if (event.origin !== import.meta.env.VITE_SH_URL || !isObject(event.data))
      return

    if ('processingEntryIds' in event.data) {
      const calendarStore = useCalendarStore()
      const ids = event.data.processingEntryIds.map(String)

      ids.forEach((id) => {
        const { dateIndex, entryIndex } = calendarStore.findEntryIndices(id)

        if (dateIndex !== -1 && entryIndex !== -1) {
          ;(
            calendarStore.entries[dateIndex][entryIndex] as ENTRY_BY_TYPE[
              | 'facebook'
              | 'instagram']
          ).studio_processing_status = 'processing'
        }
      })

      if (
        calendarStore.selectedEntry &&
        ids.includes(calendarStore.selectedEntry.id.toString())
      ) {
        ;(
          calendarStore.selectedEntry as ENTRY_BY_TYPE['facebook' | 'instagram']
        ).studio_processing_status = 'processing'
      }

      if (!calendarStore.isEntryProcessPollingActive) {
        calendarStore.resumeStudioStatusUpdatePolling()
      }
    }

    if ('spotHopper' in event.data) {
      appStore.setSpotHopperEmbedded(event.data.spotHopper)
      appStore.setInquiryFilter(event.data.inquiryFilter)
    }

    const freemium = useFreemiumPostsDialogStore()
    if ('showFreemiumPopUpInstagram' in event.data) {
      freemium.showFreemiumPopUpInstagram =
        event.data.showFreemiumPopUpInstagram
    }
    if ('showFreemiumPopUpFacebook' in event.data) {
      freemium.showFreemiumPopUpFacebook = event.data.showFreemiumPopUpFacebook
    }
  },
  false,
)

async function loginWithProvider(
  provider: 'QrCode' | 'Cognito' | 'Google',
  endpoint: string,
  payload: Record<string, any>,
) {
  const res = await useApi(endpoint).post(payload).json()

  if (!res.statusCode.value || res.statusCode.value >= 401) {
    throw {
      statusCode: res.statusCode.value,
      authProvider: provider,
    }
  }

  return res
}

let bootstrapDone = false

async function bootstrapFromQuery() {
  if (bootstrapDone) return
  bootstrapDone = true

  const route = router.currentRoute.value
  const query = route.query

  const {
    qr_auth_code,
    cognito_auth_token,
    google_id_token,
    spot_id,
    device_token,
    device_info,
  } = query

  const hasLoginToken =
    typeof qr_auth_code === 'string' ||
    typeof cognito_auth_token === 'string' ||
    typeof google_id_token === 'string'

  if (device_info) deviceInfoStore.value = String(device_info)
  if (device_token) deviceTokenStore.value = String(device_token)
  if (typeof spot_id === 'string' || typeof spot_id === 'number') {
    authStore.setSpotId(spot_id.toString())
  }
  if (authStore.spotId) authStore.setIsAuthenticated(true)

  if (hasLoginToken && !authStore.spotId) {
    try {
      let loginMethod = ''
      if (typeof qr_auth_code === 'string') {
        loginMethod = 'QrCode'
        await loginWithProvider(
          'QrCode',
          `${import.meta.env.VITE_SH_URL}/api/webapp/redeem_code`,
          { code: qr_auth_code },
        )
      }

      if (typeof cognito_auth_token === 'string') {
        loginMethod = 'Cognito'
        await loginWithProvider(
          'Cognito',
          `${import.meta.env.VITE_SH_URL}/api/webapp/redeem_ios_webapp_token`,
          { token: cognito_auth_token },
        )
      }

      if (typeof google_id_token === 'string') {
        loginMethod = 'Google'
        await loginWithProvider(
          'Google',
          `${import.meta.env.VITE_SH_URL}/api/webapp/google_sso`,
          { id_token: google_id_token },
        )
      }

      if (qr_auth_code || cognito_auth_token || google_id_token) {
        authStore.setIsAuthenticated(true)

        setTimeout(() => {
          sendLoggedInEvent('Success login', loginMethod)
        }, 3000)

        await new Promise((r) => setTimeout(r, 100))
      }
    } catch (err: any) {
      appStore.setIOSError({
        statusCode: err.statusCode,
        authProvider: err.authProvider,
        action: 'logout',
      })

      const auth_token = qr_auth_code ?? cognito_auth_token ?? google_id_token
      Sentry.captureMessage('Web App Logout Error', {
        level: 'warning',
        extra: {
          statusCode: err.statusCode,
          authProvider: err.authProvider,
          auth_token,
          device_token,
          device_info,
          page: route.path,
        },
      })

      useApi(endpoints().deleteCookies(5)).delete()
      appStore.sendErrorToiOS()
      return
    }
  }

  app.mount('#app')

  const spotsForUser = await appStore.fetchSpotsForUser(deviceTokenStore.value)

  if (
    spotsForUser.statusCode.value == 401 ||
    spotsForUser.statusCode.value == 403
  ) {
    const iosError: IOS_ERROR = {
      statusCode: spotsForUser.statusCode.value,
      action: 'logout',
    }

    Sentry.captureMessage('Web App Logout Error', {
      level: 'warning',
      extra: {
        statusCode: spotsForUser.statusCode.value,
        spots_for_user: spotsForUser.data.value,
        device_info,
        page: route.path,
      },
    })

    if (cognito_auth_token || qr_auth_code || google_id_token) {
      iosError.authProvider = cognito_auth_token
        ? 'Cognito'
        : qr_auth_code
          ? 'QrCode'
          : 'Google'
    }

    appStore.setIOSError(iosError)
    return
  }

  if (!spotsForUser.statusCode.value || spotsForUser.statusCode.value >= 500) {
    appStore.setIOSError({
      statusCode: spotsForUser.statusCode.value,
      action: 'refreshWebkit',
      url: window.location.href,
      message: 'Please try again later.',
    })
    router.push('/error')
    return
  }

  appStore.setSpotsForUser(spotsForUser.data.value.spots)
  appStore.setIsAdmin(spotsForUser.data.value.admin)

  if (!authStore.spotId) {
    let spotId = spotsForUser.data.value.spots[0]?.id ?? 5
    authStore.setSpotId(spotId)
    if (!route.query.spot_id) {
      router.replace({
        query: {
          ...route.query,
          spot_id: String(spotId),
        },
      })
    }
  }

  const spotInfoUrl = deviceTokenStore.value
    ? `${endpoints().spotInfo(authStore.spotId)}?device_id=${deviceTokenStore.value}`
    : endpoints().spotInfo(authStore.spotId)

  const { data, statusCode } = await useApi(spotInfoUrl).json()

  if (statusCode.value === 401 || statusCode.value === 403) {
    const iosError: IOS_ERROR = {
      statusCode: statusCode.value,
      action: 'logout',
    }
    appStore.setIOSError(iosError)
  }

  appStore.setSpotInfo(data.value)
  if (appStore.spotInfo) authStore.setIsAuthenticated(true)
  appStore.setSpotTz(data.value.time_zone ?? 'America/Chicago')

  if (hasLoginToken && !pendingRedirect) {
    pendingRedirect = router.currentRoute.value.fullPath
  }
}

;(async () => {
  await router.isReady()

  const query = router.currentRoute.value.query

  if ('clarity_code' in query && typeof query.clarity_code === 'string') {
    Clarity.init(query.clarity_code)
  }

  await bootstrapFromQuery()

  router.beforeEach((to, _from, next) => {
    if (to.path === '/error') return next()

    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      return next('/login')
    }

    if (appStore.iosError) {
      return next('/error')
    }

    if (typeof to.query.spot_id === 'string')
      authStore.setSpotId(to.query.spot_id)

    const authKeys = ['qr_auth_code', 'cognito_auth_token', 'google_id_token']
    const hasAuthToken = authKeys.some(
      (key) => typeof to.query[key] === 'string',
    )

    if (typeof to.query.spot_id === 'string' && hasAuthToken) {
      const newQuery = { ...to.query }
      authKeys.forEach((key) => delete newQuery[key])

      next({
        path: to.path,
        query: newQuery,
        hash: to.hash,
        replace: true,
      })
      return
    }

    next()
  })

  router.afterEach((to) => {
    if (!appStore.appReady) return

    if (to.name === 'calendar') {
      sendViewedCalendarPageEvent()
    } else {
      sendViewedPageEvent(to.name as string)
    }
  })

  if (pendingRedirect) {
    router.replace(pendingRedirect)
    pendingRedirect = null
  }
})()
